import React, {
  Reducer,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import useContextNotUndefined from '../../hooks/useContextNotUndefined';
import {
  HttpError,
  LaeringsutbyttegruppeDto,
  LaeringsutbyttegruppeEnumDto,
  LaeringsutbyttegruppeMinimalDto,
} from '../../api/generated/studiested-generated-api';
import Dropdown from '../../components/input/dropdown';
import PanelHeader from '../../components/panelheader/panelHeader';
import { useActivityContextUHA } from '../activitycontexts';
import Button from '../../components/button';
import { FakeLockedInput, Textarea, TextInput } from '../../components/input';
import * as css from './vurderingskriterier.module.scss';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import {
  CustomizableDialogButtonGroup,
  CustomizableDialogContainer,
  CustomizableDialogContent,
  CustomizableDialogFooter,
  CustomizableDialogHeader,
  CustomizeableDialogIcon,
  CustomizeableDialogTitle,
  CustomizeableDialogTitles,
} from '../../components/dialog';
import translations from './vurderingskriterier.i18n';
import { useIntl } from 'react-intl';
import { IconType } from '../../components/icon';
import {
  GenericActionWithoutPayload,
  GenericActionWithPayload,
} from '../../util';

type VurderingskriteriumModalState = {
  modalIsOpen: boolean;
  edit: boolean;
};

type VurderingskriteriumAllModalState = {
  samlingModalIsOpen?: VurderingskriteriumModalState;
  kriterieModalIsOpen?: VurderingskriteriumModalState;
};

type VurderingskriteriumsamlingData = VurderingskriteriumModalState & {
  navn?: string;
  beskrivelse?: string;
};

type ChosenVurderingskriterium = {
  id?: string;
  navn?: string;
  beskrivelse?: string;
};

type VurderingskriteriumData = VurderingskriteriumModalState &
  ChosenVurderingskriterium;

type VurderingskriterierState = VurderingskriteriumAllModalState & {
  chosenVurderingskriteriumsamling?: LaeringsutbyttegruppeDto;
  chosenVurderingskriterium?: ChosenVurderingskriterium;
  availableVurderingskriteriumsamlinger?: LaeringsutbyttegruppeMinimalDto[];
};

type NavnBeskrivelse = {
  navn: string;
  beskrivelse: string;
};

enum VurderingskriterierStateActionType {
  ADD_VURDERINGSKRITERIESAMLING,
  UPDATE_VURDERINGSKRITERIESAMLING,
  SET_CHOSEN_VURDERINGSKRITERIESAMLING,
  CREATE_KRITERIUM,
  SET_KRITERIEDATA,
  CREATE_SAMLING,
  SET_SAMLINGDATA,
  SET_AVAILABLE_SAMLINGER,
  SET_MODALS,
  RESET_MODALS,
}

type VurderingskriterierStateAction =
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.ADD_VURDERINGSKRITERIESAMLING,
      LaeringsutbyttegruppeDto
    >
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.UPDATE_VURDERINGSKRITERIESAMLING,
      LaeringsutbyttegruppeDto
    >
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.SET_CHOSEN_VURDERINGSKRITERIESAMLING,
      LaeringsutbyttegruppeDto | undefined
    >
  | GenericActionWithoutPayload<VurderingskriterierStateActionType.CREATE_KRITERIUM>
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.SET_KRITERIEDATA,
      VurderingskriteriumData
    >
  | GenericActionWithoutPayload<VurderingskriterierStateActionType.CREATE_SAMLING>
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.SET_SAMLINGDATA,
      VurderingskriteriumsamlingData
    >
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.SET_AVAILABLE_SAMLINGER,
      LaeringsutbyttegruppeMinimalDto[]
    >
  | GenericActionWithPayload<
      VurderingskriterierStateActionType.SET_MODALS,
      VurderingskriteriumAllModalState
    >
  | GenericActionWithoutPayload<VurderingskriterierStateActionType.RESET_MODALS>;

type VurderingskriterierContextProps = VurderingskriterierState & {
  dispatchVurderingskriterium: React.Dispatch<VurderingskriterierStateAction>;
};

export const VurderingskriterierContext = React.createContext<
  VurderingskriterierContextProps | undefined
>(undefined);

export const useVurderingContext = () =>
  useContextNotUndefined(VurderingskriterierContext, 'useAssessmentContext');

const vurderingskriteriumsamlingerReducer: Reducer<
  VurderingskriterierState,
  VurderingskriterierStateAction
> = (state, action) => {
  switch (action.type) {
    case VurderingskriterierStateActionType.ADD_VURDERINGSKRITERIESAMLING:
      const addedVurderingskriteriesamling = action.payload;

      return {
        ...state,
        chosenVurderingskriteriumsamling: addedVurderingskriteriesamling,
        availableVurderingskriteriumsamlinger: (
          state.availableVurderingskriteriumsamlinger || []
        ).concat(addedVurderingskriteriesamling),
      };
    case VurderingskriterierStateActionType.UPDATE_VURDERINGSKRITERIESAMLING:
      const updatedVurderingskriteriesamling = action.payload;

      return {
        ...state,
        chosenVurderingskriteriumsamling: updatedVurderingskriteriesamling,
        availableVurderingskriteriumsamlinger: (
          state.availableVurderingskriteriumsamlinger || []
        ).reduce(
          (
            acc: LaeringsutbyttegruppeMinimalDto[],
            samling: LaeringsutbyttegruppeMinimalDto,
          ) =>
            acc.concat(
              samling.id === updatedVurderingskriteriesamling.id
                ? updatedVurderingskriteriesamling
                : samling,
            ),
          [],
        ),
      };
    case VurderingskriterierStateActionType.SET_CHOSEN_VURDERINGSKRITERIESAMLING:
      return {
        ...state,
        chosenVurderingskriteriumsamling: action.payload,
      };
    case VurderingskriterierStateActionType.CREATE_KRITERIUM:
      return {
        ...state,
        chosenVurderingskriterium: undefined,
        kriterieModalIsOpen: {
          modalIsOpen: true,
          edit: false,
        },
        samlingModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
      };
    case VurderingskriterierStateActionType.SET_KRITERIEDATA:
      const kriterieData = action.payload;
      return {
        ...state,
        chosenVurderingskriterium: kriterieData,
        kriterieModalIsOpen: {
          modalIsOpen: kriterieData.modalIsOpen,
          edit: kriterieData.edit,
        },
        samlingModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
      };
    case VurderingskriterierStateActionType.CREATE_SAMLING:
      return {
        ...state,
        chosenVurderingskriteriumsamling: undefined,
        chosenVurderingskriterium: undefined,
        samlingModalIsOpen: {
          modalIsOpen: true,
          edit: false,
        },
        kriterieModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
      };
    case VurderingskriterierStateActionType.SET_SAMLINGDATA:
      const samlingData = action.payload;
      return {
        ...state,
        samlingModalIsOpen: {
          modalIsOpen: samlingData.modalIsOpen,
          edit: samlingData.edit,
        },
        kriterieModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
      };
    case VurderingskriterierStateActionType.SET_AVAILABLE_SAMLINGER:
      return {
        ...state,
        availableVurderingskriteriumsamlinger: action.payload,
      };
    case VurderingskriterierStateActionType.SET_MODALS:
      const modalState = action.payload;
      return {
        ...state,
        samlingModalIsOpen: modalState.samlingModalIsOpen,
        kriterieModalIsOpen: modalState.kriterieModalIsOpen,
      };
    case VurderingskriterierStateActionType.RESET_MODALS:
      return {
        ...state,
        samlingModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
        kriterieModalIsOpen: {
          modalIsOpen: false,
          edit: false,
        },
      };
  }
  return state;
};

const getInitNewVurderingskriterierState = (): VurderingskriterierState => ({
  availableVurderingskriteriumsamlinger: [],
});

const getEditVurderingskriteriumgruppeState = (
  availableVurderingskriteriumsamlinger: LaeringsutbyttegruppeMinimalDto[],
  chosenVurderingskriteriumsamling: LaeringsutbyttegruppeDto | undefined,
): VurderingskriterierState => ({
  availableVurderingskriteriumsamlinger,
  chosenVurderingskriteriumsamling,
});

const VurderingskriterierPanel = () => {
  const { formatMessage } = useIntl();
  const buttonEditLabel = useMemo(
    () =>
      formatMessage({
        id: translations.buttonEditVurderingskriteriumLabel.id,
      }),
    [formatMessage],
  );
  const { chosenVurderingskriteriumsamling, dispatchVurderingskriterium } =
    useVurderingContext();

  if (
    !chosenVurderingskriteriumsamling ||
    !chosenVurderingskriteriumsamling.laeringsutbytter
  )
    return <></>;

  return (
    <div data-cy={'panel'}>
      {chosenVurderingskriteriumsamling?.laeringsutbytter?.map(
        (vurderingskriterium) => (
          <PanelHeader
            title={vurderingskriterium.navn}
            key={vurderingskriterium.id}
            collapsible
          >
            <div className={css.kriterierContainer}>
              {vurderingskriterium.beskrivelse && (
                <p className={css.kriterieBeskrivelse}>
                  {vurderingskriterium.beskrivelse}
                </p>
              )}
              <div className={css.kriterieButton}>
                <Button.Quaternary
                  onClick={() => {
                    dispatchVurderingskriterium({
                      type: VurderingskriterierStateActionType.SET_KRITERIEDATA,
                      payload: {
                        id: vurderingskriterium.id,
                        navn: vurderingskriterium.navn,
                        beskrivelse: vurderingskriterium.beskrivelse,
                        modalIsOpen: true,
                        edit: true,
                      },
                    });
                  }}
                >
                  {buttonEditLabel}
                </Button.Quaternary>
              </div>
            </div>
          </PanelHeader>
        ),
      )}
    </div>
  );
};

type VurderingskriterierProps = {
  vurderingskriteriumsamlinger: LaeringsutbyttegruppeMinimalDto[] | undefined;
};

const Vurderingskriterier: React.FC<
  React.PropsWithChildren<VurderingskriterierProps>
> = ({ vurderingskriteriumsamlinger }) => {
  const { addErrorNotification } = useContext(NotificationFunctionContext);
  const {
    praksisaktivitet: { id: aktivitetId, valgtLaeringsutbyttegruppe },
  } = useActivityContextUHA();

  const { praksisaktivitetVurderingApi } = useContext(StudiestedApiContext);

  const [vurderingskriteriumsamlingState, dispatchVurderingskriteriumsamling] =
    useReducer(
      vurderingskriteriumsamlingerReducer,
      vurderingskriteriumsamlinger
        ? getEditVurderingskriteriumgruppeState(
            vurderingskriteriumsamlinger,
            valgtLaeringsutbyttegruppe,
          )
        : getInitNewVurderingskriterierState(),
    );
  const {
    availableVurderingskriteriumsamlinger,
    chosenVurderingskriteriumsamling,
  } = useMemo(
    () => vurderingskriteriumsamlingState,
    [vurderingskriteriumsamlingState],
  );

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      dropdownLabel: formatMessage({ id: translations.dropdownLabel.id }),
      dropdownPlaceholder: formatMessage({
        id: translations.dropdownPlaceholder.id,
      }),
      buttonCreatesamlingLabel: formatMessage({
        id: translations.buttonCreatesamlingLabel.id,
      }),
      buttonEditsamlingLabel: formatMessage({
        id: translations.buttonEditsamlingLabel.id,
      }),
      buttonCreateVurderingskriteriumLabel: formatMessage({
        id: translations.buttonCreateVurderingskriteriumLabel.id,
      }),
    }),
    [formatMessage],
  );

  const onClose = () => {
    dispatchVurderingskriteriumsamling({
      type: VurderingskriterierStateActionType.RESET_MODALS,
    });
  };

  const handleDropdownChange = (
    laeringsutbyttegruppeMinimalDto: LaeringsutbyttegruppeMinimalDto,
  ) => {
    praksisaktivitetVurderingApi
      .praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppePut({
        pathParams: {
          aktivitetId,
        },
        laeringsutbyttegruppeMinimalDto,
      })
      .then((res) => {
        dispatchVurderingskriteriumsamling({
          type: VurderingskriterierStateActionType.SET_CHOSEN_VURDERINGSKRITERIESAMLING,
          payload: res,
        });
      })
      .catch((err: HttpError) => addErrorNotification(err.body));
  };

  const vurderingskriterierContextValue = useMemo(
    () => ({
      ...vurderingskriteriumsamlingState,
      dispatchVurderingskriterium: dispatchVurderingskriteriumsamling,
    }),
    [vurderingskriteriumsamlingState],
  );

  return (
    <VurderingskriterierContext.Provider
      value={vurderingskriterierContextValue}
    >
      <div className={css.container}>
        <div className={css.dropdownRow}>
          <Dropdown
            id={'vurderingskriteriumsamling-select'}
            onChange={(_event, value: LaeringsutbyttegruppeMinimalDto) =>
              handleDropdownChange(value)
            }
            options={availableVurderingskriteriumsamlinger}
            label={labels.dropdownLabel}
            selectedOption={chosenVurderingskriteriumsamling}
            placeholder={labels.dropdownPlaceholder}
            className={css.dropdownMenu}
          />
          <div className={css.buttonGroup}>
            <Button.Secondary
              data-cy={'vurderingskriteriumsamling-create-button'}
              onClick={() => {
                dispatchVurderingskriteriumsamling({
                  type: VurderingskriterierStateActionType.CREATE_SAMLING,
                });
              }}
            >
              {labels.buttonCreatesamlingLabel}
            </Button.Secondary>
            {vurderingskriteriumsamlinger && (
              <Button.Secondary
                data-cy={'vurderingskriteriumsamling-edit-button'}
                disabled={!chosenVurderingskriteriumsamling?.id}
                onClick={() => {
                  dispatchVurderingskriteriumsamling({
                    type: VurderingskriterierStateActionType.SET_SAMLINGDATA,
                    payload: {
                      modalIsOpen: true,
                      edit: true,
                    },
                  });
                }}
              >
                {labels.buttonEditsamlingLabel}
              </Button.Secondary>
            )}
          </div>
        </div>
        {chosenVurderingskriteriumsamling?.beskrivelse && (
          <p
            className={css.gruppeBeskrivelse}
            data-cy={'vurderingskriteriumsamling-beskrivelse'}
          >
            {chosenVurderingskriteriumsamling?.beskrivelse}
          </p>
        )}
        <VurderingskriterierPanel />
        <Button.Secondary
          data-cy={'vurderingskriterium-create-button'}
          disabled={!chosenVurderingskriteriumsamling?.id}
          className={css.createButton}
          onClick={() => {
            dispatchVurderingskriteriumsamling({
              type: VurderingskriterierStateActionType.CREATE_KRITERIUM,
            });
          }}
        >
          {labels.buttonCreateVurderingskriteriumLabel}
        </Button.Secondary>
        <VurderingskriteriumgruppeModalWrapper onClose={onClose} />
      </div>
    </VurderingskriterierContext.Provider>
  );
};

type VurderingskriterierWrapperProps = {
  onClose: () => void;
};

const navnIsValid = (navn: string): boolean => navn.length > 0;
const everyNavnIsValid = (
  updatedVurderingskriterier: LaeringsutbyttegruppeDto,
) =>
  navnIsValid(updatedVurderingskriterier.navn) &&
  updatedVurderingskriterier.laeringsutbytter?.every(({ navn }) =>
    navnIsValid(navn),
  );

const VurderingskriteriumgruppeModalWrapper: React.FC<
  React.PropsWithChildren<VurderingskriterierWrapperProps>
> = ({ onClose }) => {
  const {
    praksisaktivitet: { id: aktivitetId },
  } = useActivityContextUHA();

  const {
    chosenVurderingskriteriumsamling,
    chosenVurderingskriterium,
    dispatchVurderingskriterium,
    samlingModalIsOpen,
    kriterieModalIsOpen,
  } = useVurderingContext();

  const { praksisaktivitetVurderingApi } = useContext(StudiestedApiContext);

  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vurderingskriteriumsamlingCreateSuccess: formatMessage({
        id: translations.vurderingskriteriumsamlingCreateSuccess.id,
      }),
      vurderingskriteriumsamlingEditSuccess: formatMessage({
        id: translations.vurderingskriteriumsamlingEditSuccess.id,
      }),
      vurderingskriteriumsamlingDeleteSuccess: formatMessage({
        id: translations.vurderingskriteriumsamlingDeleteSuccess.id,
      }),
      vurderingskriteriumCreateSuccess: formatMessage({
        id: translations.vurderingskriteriumCreateSuccess.id,
      }),
      vurderingskriteriumEditSuccess: formatMessage({
        id: translations.vurderingskriteriumEditSuccess.id,
      }),
      vurderingskriteriumDeleteSuccess: formatMessage({
        id: translations.vurderingskriteriumDeleteSuccess.id,
      }),
      vurderingskriteriumsamlingModalTitleEdit: formatMessage({
        id: translations.vurderingskriteriumsamlingModalTitleEdit.id,
      }),
      vurderingskriteriumsamlingModalTitleCreate: formatMessage({
        id: translations.vurderingskriteriumsamlingModalTitleCreate.id,
      }),
      vurderingskriteriumsamlingModalNavnFieldLabel: formatMessage({
        id: translations.vurderingskriteriumsamlingModalNavnFieldLabel.id,
      }),
      vurderingskriteriumModalTitleEdit: formatMessage({
        id: translations.vurderingskriteriumModalTitleEdit.id,
      }),
      vurderingskriteriumModalTitleCreate: formatMessage({
        id: translations.vurderingskriteriumModalTitleCreate.id,
      }),
      vurderingskriteriumModalNavnFieldLabel: formatMessage({
        id: translations.vurderingskriteriumModalNavnFieldLabel.id,
      }),
      beskrivelseFieldLabel: formatMessage({
        id: translations.beskrivelseFieldLabel.id,
      }),
      vurderingsmetodeFieldLabel: formatMessage({
        id: translations.vurderingsmetodeFieldLabel.id,
      }),
      buttonDeleteLabel: formatMessage({
        id: translations.buttonDeleteLabel.id,
      }),
      buttonCancelLabel: formatMessage({
        id: translations.buttonCancelLabel.id,
      }),
      buttonSaveLabel: formatMessage({
        id: translations.buttonSaveLabel.id,
      }),
    }),
    [formatMessage],
  );

  const handlePost = ({ navn, beskrivelse }: NavnBeskrivelse) => {
    if (navnIsValid(navn)) {
      praksisaktivitetVurderingApi
        .praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppePost({
          pathParams: {
            aktivitetId,
          },
          laeringsutbyttegruppeNyDto: {
            navn,
            beskrivelse,
            laeringsutbytter: [],
            vurderingsmetode: LaeringsutbyttegruppeEnumDto.Fritekst,
          },
        })
        .then((res) => {
          addSuccessNotification(
            labels.vurderingskriteriumsamlingCreateSuccess,
          );
          dispatchVurderingskriterium({
            type: VurderingskriterierStateActionType.ADD_VURDERINGSKRITERIESAMLING,
            payload: res,
          });
          onClose();
        })
        .catch((err: HttpError) => addErrorNotification(err.body));
    }
  };

  const handlePut = (
    updatedVurderingskriterier: LaeringsutbyttegruppeDto,
    successMessage: string,
  ) => {
    if (everyNavnIsValid(updatedVurderingskriterier)) {
      praksisaktivitetVurderingApi
        .praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppeLaeringsutbyttegruppeIdPut(
          {
            pathParams: {
              aktivitetId,
              laeringsutbyttegruppeId: updatedVurderingskriterier.id,
            },
            laeringsutbyttegruppeDto: updatedVurderingskriterier,
          },
        )
        .then((res) => {
          dispatchVurderingskriterium({
            type: VurderingskriterierStateActionType.UPDATE_VURDERINGSKRITERIESAMLING,
            payload: res,
          });
          addSuccessNotification(successMessage);
          onClose();
        })
        .catch((err: HttpError) => addErrorNotification(err.body));
    }
  };

  const handleDelete = () => {
    praksisaktivitetVurderingApi
      .praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppeLaeringsutbyttegruppeIdDelete(
        {
          pathParams: {
            aktivitetId,
            laeringsutbyttegruppeId:
              chosenVurderingskriteriumsamling?.id as string,
          },
        },
      )
      .then(() => {
        praksisaktivitetVurderingApi
          .praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppeGet({
            pathParams: {
              aktivitetId,
            },
          })
          .then((res) => {
            dispatchVurderingskriterium({
              type: VurderingskriterierStateActionType.SET_AVAILABLE_SAMLINGER,
              payload: res,
            });
            addSuccessNotification(
              labels.vurderingskriteriumsamlingDeleteSuccess,
            );
            dispatchVurderingskriterium({
              type: VurderingskriterierStateActionType.SET_CHOSEN_VURDERINGSKRITERIESAMLING,
              payload: undefined,
            });
            onClose();
          })
          .catch((err: HttpError) => addErrorNotification(err.body));
      })
      .catch((err: HttpError) => addErrorNotification(err.body));
  };

  const handleUpdateSamling = ({ navn, beskrivelse }: NavnBeskrivelse) => {
    if (!chosenVurderingskriteriumsamling) return;

    const updatedGruppe = {
      ...chosenVurderingskriteriumsamling,
      navn,
      beskrivelse,
    };
    handlePut(updatedGruppe, labels.vurderingskriteriumsamlingEditSuccess);
  };

  const handleUpdateKriterie = ({ navn, beskrivelse }: NavnBeskrivelse) => {
    if (
      chosenVurderingskriterium &&
      chosenVurderingskriteriumsamling?.laeringsutbytter
    ) {
      const updatedVurderingskriterier = {
        ...chosenVurderingskriteriumsamling,
        laeringsutbytter:
          chosenVurderingskriteriumsamling?.laeringsutbytter.map(
            (vurderingskriterium) =>
              vurderingskriterium.id === chosenVurderingskriterium.id
                ? {
                    ...vurderingskriterium,
                    navn,
                    beskrivelse,
                  }
                : vurderingskriterium,
          ),
      };
      handlePut(
        updatedVurderingskriterier,
        labels.vurderingskriteriumEditSuccess,
      );
    }
  };

  const handleCreateKriterie = ({ navn, beskrivelse }: NavnBeskrivelse) => {
    if (!chosenVurderingskriteriumsamling) return;

    const updatedVurderingskriterier = {
      ...chosenVurderingskriteriumsamling,
      laeringsutbytter: [
        ...(chosenVurderingskriteriumsamling?.laeringsutbytter || []),
        {
          navn,
          beskrivelse,
        },
      ],
    };

    handlePut(
      updatedVurderingskriterier,
      labels.vurderingskriteriumCreateSuccess,
    );
  };

  const handleDeleteKriterie = () => {
    if (!chosenVurderingskriteriumsamling) return;

    const updatedVurderingskriterier = {
      ...chosenVurderingskriteriumsamling,
      laeringsutbytter: chosenVurderingskriteriumsamling?.laeringsutbytter
        ? chosenVurderingskriteriumsamling.laeringsutbytter.filter(
            (vurderingskriterium) =>
              vurderingskriterium.id !== chosenVurderingskriterium?.id,
          )
        : [],
    };
    handlePut(
      updatedVurderingskriterier,
      labels.vurderingskriteriumDeleteSuccess,
    );
  };

  const handleKriteriumsamlingModalSave = (
    navnBeskrivelse: NavnBeskrivelse,
  ) => {
    samlingModalIsOpen?.edit
      ? handleUpdateSamling(navnBeskrivelse)
      : handlePost(navnBeskrivelse);
  };

  const handleKriteriumModalSave = (navnBeskrivelse: NavnBeskrivelse) => {
    kriterieModalIsOpen?.edit
      ? handleUpdateKriterie(navnBeskrivelse)
      : handleCreateKriterie(navnBeskrivelse);
  };

  return (
    <>
      {samlingModalIsOpen?.modalIsOpen && (
        <VurderingskriteriumsamlingModal
          onClose={onClose}
          handleSave={handleKriteriumsamlingModalSave}
          handleDelete={handleDelete}
        />
      )}
      {kriterieModalIsOpen?.modalIsOpen && (
        <VurderingskriteriumModal
          onClose={onClose}
          handleSave={handleKriteriumModalSave}
          handleDelete={handleDeleteKriterie}
        />
      )}
    </>
  );
};

type ModalProps = {
  handleSave: (value: NavnBeskrivelse) => void;
  onClose: () => void;
  handleDelete: () => void;
};

const VurderingskriteriumsamlingModal: React.FC<
  React.PropsWithChildren<ModalProps>
> = ({ onClose, handleSave, handleDelete }) => {
  const { samlingModalIsOpen, chosenVurderingskriteriumsamling } =
    useVurderingContext();

  const [navn, setNavn] = useState<string>(
    chosenVurderingskriteriumsamling?.navn || '',
  );
  const [beskrivelse, setBeskrivelse] = useState<string>(
    chosenVurderingskriteriumsamling?.beskrivelse || '',
  );

  const {
    praksisaktivitet: {
      emne: { navn: emnenavn },
    },
  } = useActivityContextUHA();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vurderingskriteriumsamlingModalTitleEdit: formatMessage({
        id: translations.vurderingskriteriumsamlingModalTitleEdit.id,
      }),
      vurderingskriteriumsamlingModalTitleCreate: formatMessage({
        id: translations.vurderingskriteriumsamlingModalTitleCreate.id,
      }),
      vurderingskriteriumsamlingModalNavnFieldLabel: formatMessage({
        id: translations.vurderingskriteriumsamlingModalNavnFieldLabel.id,
      }),
      beskrivelseFieldLabel: formatMessage({
        id: translations.beskrivelseFieldLabel.id,
      }),
      vurderingsmetodeFieldLabel: formatMessage({
        id: translations.vurderingsmetodeFieldLabel.id,
      }),
      vurderingsmetodeValueLabel: formatMessage({
        id: translations.vurderingsmetodeValueLabel.id,
      }),
      buttonDeleteLabel: formatMessage({
        id: translations.buttonDeleteLabel.id,
      }),
      buttonCancelLabel: formatMessage({
        id: translations.buttonCancelLabel.id,
      }),
      buttonSaveLabel: formatMessage({
        id: translations.buttonSaveLabel.id,
      }),
    }),
    [formatMessage],
  );

  const titleLabel = useMemo(
    () =>
      `${
        samlingModalIsOpen?.edit
          ? labels.vurderingskriteriumsamlingModalTitleEdit
          : labels.vurderingskriteriumsamlingModalTitleCreate
      } ${emnenavn}`,
    [
      samlingModalIsOpen?.edit,
      labels.vurderingskriteriumsamlingModalTitleEdit,
      labels.vurderingskriteriumsamlingModalTitleCreate,
      emnenavn,
    ],
  );

  const submitDisabled = useMemo(() => !navnIsValid(navn), [navn]);

  return (
    <CustomizableDialogContainer
      isOpen
      onCancel={onClose}
      className={css.modal}
    >
      <CustomizableDialogHeader>
        <CustomizeableDialogIcon type={IconType.Info} />
        <CustomizeableDialogTitles>
          <CustomizeableDialogTitle>{titleLabel}</CustomizeableDialogTitle>
        </CustomizeableDialogTitles>
      </CustomizableDialogHeader>
      <CustomizableDialogContent className={css.modalContainer}>
        <div className={css.row}>
          <div className={css.rowItem}>
            <TextInput
              htmlId={'vurderingskriteriumsamling-navn'}
              label={labels.vurderingskriteriumsamlingModalNavnFieldLabel}
              onChange={setNavn}
              value={navn}
              required
            />
          </div>
          <div className={css.rowItem}>
            <FakeLockedInput
              label={labels.vurderingsmetodeFieldLabel}
              value={labels.vurderingsmetodeValueLabel}
              dataCy={'vurderingskriteriumsamling-vurderingsmetode'}
            />
          </div>
        </div>
        <div>
          <Textarea
            className={css.textArea}
            htmlId={'vurderingskriteriumsamling-beskrivelse'}
            defaultValue={beskrivelse}
            label={labels.beskrivelseFieldLabel}
            onChange={(event) => setBeskrivelse(event.currentTarget.value)}
          />
        </div>
      </CustomizableDialogContent>
      <CustomizableDialogFooter>
        <Button.Quaternary
          data-cy={'vurderingskriteriumsamling-delete-button'}
          onClick={handleDelete}
          disabled={!samlingModalIsOpen?.edit}
        >
          {labels.buttonDeleteLabel}
        </Button.Quaternary>
        <CustomizableDialogButtonGroup>
          <Button.Quaternary onClick={onClose}>
            {labels.buttonCancelLabel}
          </Button.Quaternary>
          <Button.Secondary
            data-cy={'vurderingskriteriumsamling-save-button'}
            onClick={() => handleSave({ navn, beskrivelse })}
            disabled={submitDisabled}
          >
            {labels.buttonSaveLabel}
          </Button.Secondary>
        </CustomizableDialogButtonGroup>
      </CustomizableDialogFooter>
    </CustomizableDialogContainer>
  );
};

const VurderingskriteriumModal: React.FC<
  React.PropsWithChildren<ModalProps>
> = ({ onClose, handleSave, handleDelete }) => {
  const { kriterieModalIsOpen, chosenVurderingskriterium } =
    useVurderingContext();

  const [navn, setNavn] = useState<string>(
    chosenVurderingskriterium?.navn || '',
  );
  const [beskrivelse, setBeskrivelse] = useState<string>(
    chosenVurderingskriterium?.beskrivelse || '',
  );

  const {
    praksisaktivitet: {
      emne: { navn: emnenavn },
    },
  } = useActivityContextUHA();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vurderingskriteriumModalTitleEdit: formatMessage({
        id: translations.vurderingskriteriumModalTitleEdit.id,
      }),
      vurderingskriteriumModalTitleCreate: formatMessage({
        id: translations.vurderingskriteriumModalTitleCreate.id,
      }),
      vurderingskriteriumModalNavnFieldLabel: formatMessage({
        id: translations.vurderingskriteriumModalNavnFieldLabel.id,
      }),
      beskrivelseFieldLabel: formatMessage({
        id: translations.beskrivelseFieldLabel.id,
      }),
      vurderingsmetodeFieldLabel: formatMessage({
        id: translations.vurderingsmetodeFieldLabel.id,
      }),
      buttonDeleteLabel: formatMessage({
        id: translations.buttonDeleteLabel.id,
      }),
      buttonCancelLabel: formatMessage({
        id: translations.buttonCancelLabel.id,
      }),
      buttonSaveLabel: formatMessage({
        id: translations.buttonSaveLabel.id,
      }),
    }),
    [formatMessage],
  );

  const titleLabel = useMemo(
    () =>
      `${
        kriterieModalIsOpen?.edit
          ? labels.vurderingskriteriumModalTitleEdit
          : labels.vurderingskriteriumModalTitleCreate
      } ${emnenavn}`,
    [
      kriterieModalIsOpen?.edit,
      labels.vurderingskriteriumModalTitleEdit,
      labels.vurderingskriteriumModalTitleCreate,
      emnenavn,
    ],
  );

  const submitDisabled = useMemo(() => !navnIsValid(navn), [navn]);

  return (
    <CustomizableDialogContainer
      isOpen
      onCancel={onClose}
      className={css.modal}
    >
      <CustomizableDialogHeader>
        <CustomizeableDialogIcon type={IconType.Info} />
        <CustomizeableDialogTitles>
          <CustomizeableDialogTitle>{titleLabel}</CustomizeableDialogTitle>
        </CustomizeableDialogTitles>
      </CustomizableDialogHeader>
      <CustomizableDialogContent>
        <div className={css.modalContainer}>
          <div className={css.row}>
            <TextInput
              htmlId={'vurderingskriterium-navn'}
              label={labels.vurderingskriteriumModalNavnFieldLabel}
              onChange={setNavn}
              value={navn}
              required
            />
          </div>
          <div>
            <Textarea
              className={css.textArea}
              htmlId={'vurderingskriterium-beskrivelse'}
              defaultValue={beskrivelse}
              onChange={(event) => setBeskrivelse(event.currentTarget.value)}
              label={labels.beskrivelseFieldLabel}
            />
          </div>
        </div>
      </CustomizableDialogContent>
      <CustomizableDialogFooter>
        <Button.Quaternary
          data-cy={'vurderingskriterium-delete-button'}
          onClick={handleDelete}
          disabled={!kriterieModalIsOpen?.edit}
        >
          {labels.buttonDeleteLabel}
        </Button.Quaternary>
        <CustomizableDialogButtonGroup>
          <Button.Quaternary onClick={onClose}>
            {labels.buttonCancelLabel}
          </Button.Quaternary>
          <Button.Secondary
            data-cy={'vurderingskriterium-save-button'}
            onClick={() => handleSave({ navn, beskrivelse })}
            disabled={submitDisabled}
          >
            {labels.buttonSaveLabel}
          </Button.Secondary>
        </CustomizableDialogButtonGroup>
      </CustomizableDialogFooter>
    </CustomizableDialogContainer>
  );
};

export default Vurderingskriterier;
